import { useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import {
  ArrowLeft,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  MessageCircleQuestion,
  Loader2,
  Maximize2,
  Plus,
  RotateCw,
  Trash2,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { toast } from "sonner";
import { apiDelete, apiGet, apiPost, queryClient } from "@/lib/api";
import {
  countryConfigOrDefault,
  taxLabel,
  vendorTaxIdLabel,
  type CountryConfig,
} from "@/lib/country";
import { useAccountant } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { EmailComposer } from "@/components/shared/email-composer";
import { QueryError } from "@/components/shared/query-error";
import { cn } from "@/lib/utils";

function formatCategoryName(name: string): string {
  if (!name) return name;
  return name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function isPdfPath(path: string | null | undefined): boolean {
  if (!path) return false;
  return /\.pdf(\?|$)/i.test(path);
}

interface ReviewReceipt {
  id: string;
  file_path: string;
  presigned_url: string;
  confirmed: boolean;
  has_raw_data: boolean;
  total: string | null;
  tax: string | null;
  date: string | null;
  vendor_name: string;
  vendor_tax_id: string;
  note: string;
  category_name: string;
  uploaded_by_email: string | null;
  created: string;
}

interface ReviewListResponse {
  results: ReviewReceipt[];
  total_count: number;
}

interface ClientMeta {
  id: string;
  display_name: string;
  subscription_status: string;
  /** Same endpoint as the client detail page, so the tax wording here matches. */
  country_config?: CountryConfig;
}

export function ReviewRoute() {
  const { id: userId } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const atParam = searchParams.get("at");

  const { data: accountant } = useAccountant();
  const { data: clientMeta } = useQuery({
    queryKey: ["accountant-client", userId],
    queryFn: () => apiGet<ClientMeta>(`/api/accountant/users/${userId}`),
    enabled: !!userId,
  });
  const isSubscribed =
    clientMeta?.subscription_status === "subscribed" ||
    clientMeta?.subscription_status === "cancelled";
  // Tax wording follows the client's country: GST/ABN in AU, VAT/VAT Number
  // in the UK, Sales Tax/EIN in the US.
  const clientCountry = countryConfigOrDefault(clientMeta?.country_config);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["accountant-receipts-tray", userId],
    queryFn: () =>
      apiGet<ReviewListResponse>(
        `/api/accountant/users/${userId}/receipts?status=tray&limit=100`,
      ),
    enabled: !!userId,
  });

  const queue = useMemo(
    () => (data?.results ?? []).filter((r) => r.has_raw_data && !r.confirmed),
    [data],
  );

  // Pick the selected receipt — prefer the ?at=<id> param; fall back to first.
  const selectedIdx = useMemo(() => {
    if (!queue.length) return -1;
    if (atParam) {
      const idx = queue.findIndex((r) => r.id === atParam);
      if (idx !== -1) return idx;
    }
    return 0;
  }, [queue, atParam]);

  const selected = selectedIdx >= 0 ? queue[selectedIdx] : null;

  const setAt = useCallback(
    (id: string | undefined) => {
      const next = new URLSearchParams(searchParams);
      if (id) next.set("at", id);
      else next.delete("at");
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // Sync ?at= to the first queue item once loaded
  useEffect(() => {
    if (queue.length && !atParam) setAt(queue[0].id);
  }, [queue, atParam, setAt]);

  const goto = useCallback(
    (delta: number) => {
      if (!queue.length) return;
      const next =
        queue[Math.max(0, Math.min(queue.length - 1, selectedIdx + delta))];
      if (next) setAt(next.id);
    },
    [queue, selectedIdx, setAt],
  );

  const goBack = useCallback(() => {
    navigate(`/accountant/clients/${userId}`);
  }, [navigate, userId]);

  const [editForm, setEditForm] = useState({
    vendor_name: "",
    vendor_tax_id: "",
    total: "",
    tax: "",
    date: "",
    note: "",
    category_name: "",
  });
  const vendorId = useId();
  const vendorTaxIdId = useId();
  const dateId = useId();
  const totalId = useId();
  const taxId = useId();
  const categoryLabelId = useId();
  const noteId = useId();
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [imgErrored, setImgErrored] = useState(false);
  const [queryOpen, setQueryOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [categorySearch, setCategorySearch] = useState("");
  const [confirmAllOpen, setConfirmAllOpen] = useState(false);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  // Reset on id change during render (React's "reset state when prop changes" pattern).
  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null);
  const nextSelectedId = selected?.id ?? null;
  if (nextSelectedId !== lastSelectedId) {
    setLastSelectedId(nextSelectedId);
    setZoom(1);
    setRotation(0);
    setImgErrored(false);
    if (selected) {
      setEditForm({
        vendor_name: selected.vendor_name || "",
        vendor_tax_id: selected.vendor_tax_id || "",
        total: selected.total || "",
        tax: selected.tax || "",
        date: selected.date || "",
        note: selected.note || "",
        category_name: selected.category_name || "",
      });
    }
  }

  const advance = useCallback(() => {
    // After confirming/discarding, the current receipt leaves the queue. Next
    // "selected" is whatever is now at the same index. If we're at the end,
    // go back to the client page.
    const remaining = queue.length - 1;
    if (remaining <= 0) {
      goBack();
      toast.success("Batch done");
      return;
    }
    const nextIdx = Math.min(selectedIdx, remaining - 1);
    const next = queue[nextIdx === selectedIdx ? selectedIdx + 1 : nextIdx];
    if (next) setAt(next.id);
  }, [queue, selectedIdx, goBack, setAt]);

  const editFormPayload = () => {
    // DRF's DateField rejects empty strings, so strip any blank field before
    // posting. Accepts explicit nulls via allow_null=True on the serializer.
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(editForm)) {
      if (v !== "") out[k] = v;
    }
    return out;
  };

  const confirmMutation = useMutation({
    mutationFn: () =>
      apiPost(
        `/api/accountant/users/${userId}/receipts/${selected?.id}/confirm`,
        editFormPayload(),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["accountant-receipts-tray", userId],
      });
      queryClient.invalidateQueries({
        queryKey: ["accountant-receipts", userId],
      });
      advance();
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Could not confirm"),
  });

  const discardMutation = useMutation({
    mutationFn: () =>
      apiDelete(`/api/accountant/users/${userId}/receipts/${selected?.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["accountant-receipts-tray", userId],
      });
      queryClient.invalidateQueries({
        queryKey: ["accountant-receipts", userId],
      });
      advance();
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Could not discard"),
  });

  const queryMutation = useMutation({
    mutationFn: ({
      message,
      subject,
    }: {
      message: string;
      subject?: string;
    }) =>
      apiPost(
        `/api/accountant/users/${userId}/receipts/${selected?.id}/query`,
        { message, subject },
      ),
    onSuccess: () => {
      toast.success("Question sent to client");
      setQueryOpen(false);
      queryClient.invalidateQueries({
        queryKey: ["accountant-messages", userId],
      });
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Could not send"),
  });

  const batchConfirmMutation = useMutation({
    mutationFn: () =>
      apiPost<{
        confirmed_ids: string[];
        rejected_ids: string[];
        detail: string;
      }>(`/api/accountant/users/${userId}/receipts/batch-confirm`, {
        receipt_ids: queue.map((r) => r.id),
      }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: ["accountant-receipts-tray", userId],
      });
      queryClient.invalidateQueries({
        queryKey: ["accountant-receipts", userId],
      });
      setConfirmAllOpen(false);
      if (res.rejected_ids.length) {
        // Partial / full rejection (e.g. unsubscribed client hit the 10-cap).
        // Keep the accountant on /review so the remaining receipts are visible
        // and it's obvious what didn't go through.
        toast.warning(res.detail || "Some receipts could not be confirmed");
        return;
      }
      toast.success(`Confirmed ${res.confirmed_ids.length} receipts`);
      goBack();
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Could not confirm"),
  });

  const createCategoryMutation = useMutation({
    mutationFn: (name: string) =>
      apiPost("/api/accountant/categories", { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accountant-me"] });
    },
    onError: (err) =>
      toast.error(
        err instanceof Error ? err.message : "Could not create category",
      ),
  });

  // The accountant only sees their own categories in the picker. If the
  // receipt already carries a category that's NOT in the accountant's list
  // (e.g. the client created it on mobile before being invited), include it
  // anyway so the accountant can see what it is and re-select it.
  const baseCategories = accountant?.categories ?? [];
  const currentName = editForm.category_name;
  const currentInList =
    !currentName || baseCategories.some((c) => c.name === currentName);
  const pickerCategories = currentInList
    ? baseCategories
    : [
        ...baseCategories,
        { id: `receipt-current-${currentName}`, name: currentName },
      ];
  const filteredCategories = pickerCategories.filter(
    (c) =>
      !categorySearch ||
      formatCategoryName(c.name)
        .toLowerCase()
        .includes(categorySearch.toLowerCase()),
  );
  const showCreateCategory =
    categorySearch.trim() !== "" &&
    !pickerCategories.some(
      (c) =>
        formatCategoryName(c.name).toLowerCase() ===
        categorySearch.trim().toLowerCase(),
    );

  // NOTE: intentionally no keyboard shortcuts in this flow — see
  // taxr/CLAUDE.md "Accountant portal UX" for the no-hotkeys policy.

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-6 w-56" />
        <div className="flex gap-4">
          <Skeleton className="h-[60vh] flex-1 rounded-lg" />
          <div className="w-80 space-y-3">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        </div>
      </div>
    );
  }
  if (isError) {
    return (
      <div className="p-6">
        <QueryError
          error={error}
          message="Couldn't load review queue"
          onRetry={() =>
            queryClient.invalidateQueries({
              queryKey: ["accountant-receipts-tray", userId],
            })
          }
        />
      </div>
    );
  }
  if (!queue.length) {
    return (
      <div className="p-6 max-w-md mx-auto text-center space-y-3">
        <p className="text-sm text-muted-foreground">
          Nothing left to review. Head back to the client page.
        </p>
        <Button onClick={goBack}>Back</Button>
      </div>
    );
  }

  // Queue has loaded and has items, but the ?at= param points to a receipt
  // that is no longer in the review queue (confirmed or reassigned elsewhere).
  if (atParam && selectedIdx === -1) {
    return (
      <div className="p-6 max-w-md mx-auto text-center space-y-3">
        <p className="text-sm text-muted-foreground">
          This receipt is no longer in the review queue. It may have been
          confirmed or reassigned.
        </p>
        <Button
          variant="secondary"
          onClick={() => {
            const next = new URLSearchParams(searchParams);
            next.delete("at");
            setSearchParams(next, { replace: true });
          }}
        >
          Back to queue
        </Button>
      </div>
    );
  }

  if (!selected) return null;

  return (
    <div className="flex flex-col min-h-screen bg-muted/20">
      {/* Header */}
      <header className="flex items-center justify-between gap-3 border-b bg-background px-4 py-3">
        <div className="flex items-center gap-2 min-w-0">
          <Button variant="ghost" size="sm" onClick={goBack} aria-label="Back">
            <ArrowLeft className="size-4" aria-hidden />
          </Button>
          <div className="min-w-0">
            <p className="text-sm font-medium truncate">
              Reviewing {clientMeta?.display_name || "client"}
            </p>
            <p className="text-xs text-muted-foreground">
              {selectedIdx + 1} of {queue.length}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setConfirmAllOpen(true)}
            disabled={batchConfirmMutation.isPending}
          >
            Confirm all {queue.length}
          </Button>
        </div>
      </header>

      {/* Body */}
      <div className="flex-1 grid lg:grid-cols-[minmax(0,1fr)_380px] gap-4 p-4 max-w-7xl mx-auto w-full">
        {/* Image pane */}
        <div className="rounded-lg border bg-background overflow-hidden flex flex-col">
          <div className="flex items-center gap-1 px-2 py-1.5 border-b bg-muted/50">
            {/* Zoom / rotate only make sense for raster images — the PDF
                iframe has its own built-in toolbar. */}
            {!isPdfPath(selected.file_path) && (
              <>
                <Button
                  variant="ghost"
                  size="icon-xs"
                  aria-label="Zoom in"
                  onClick={() => setZoom((z) => Math.min(z + 0.5, 3))}
                >
                  <ZoomIn className="size-3.5" aria-hidden />
                </Button>
                <Button
                  variant="ghost"
                  size="icon-xs"
                  aria-label="Zoom out"
                  onClick={() => setZoom((z) => Math.max(z - 0.5, 0.5))}
                >
                  <ZoomOut className="size-3.5" aria-hidden />
                </Button>
                <span
                  className="text-[10px] text-muted-foreground tabular-nums w-8 text-center"
                  aria-live="polite"
                >
                  {Math.round(zoom * 100)}%
                </span>
              </>
            )}
            {!isPdfPath(selected.file_path) && (
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label="Rotate 90 degrees"
                onClick={() => setRotation((r) => (r + 90) % 360)}
              >
                <RotateCw className="size-3.5" aria-hidden />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label="View full preview"
              className="ml-auto"
              onClick={() => setFullscreen(true)}
            >
              <Maximize2 className="size-3.5" aria-hidden />
            </Button>
          </div>
          <div className="flex-1 overflow-auto flex items-center justify-center p-4 bg-muted/30 min-h-[400px]">
            {imgErrored ? (
              <div className="text-center text-sm text-muted-foreground p-8 space-y-2">
                <p>Preview unavailable</p>
                <p className="text-xs opacity-70">
                  The underlying file couldn't be loaded.
                </p>
              </div>
            ) : isPdfPath(selected.file_path) ? (
              <iframe
                key={selected.id}
                src={selected.presigned_url}
                title="Receipt PDF"
                className="w-full min-h-[65vh] rounded border bg-background"
                onError={() => setImgErrored(true)}
                onLoad={() => setImgErrored(false)}
              />
            ) : (
              <img
                // key forces a fresh <img> when navigating between receipts so
                // a previous onError state doesn't leak across selections.
                key={selected.id}
                src={selected.presigned_url}
                alt="Receipt"
                className="max-w-full max-h-[65vh] object-contain transition-transform cursor-zoom-in"
                style={{
                  transform: `scale(${zoom}) rotate(${rotation}deg)`,
                  transformOrigin: "center center",
                }}
                onClick={() => setFullscreen(true)}
                onError={() => setImgErrored(true)}
                onLoad={() => setImgErrored(false)}
              />
            )}
          </div>
        </div>

        {/* Form pane */}
        <div className="rounded-lg border bg-background p-4 space-y-4 h-max">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor={vendorId} className="text-xs">Vendor</Label>
              <Input
                id={vendorId}
                value={editForm.vendor_name}
                onChange={(e) =>
                  setEditForm((f) => ({
                    ...f,
                    vendor_name: (e.target as HTMLInputElement).value,
                  }))
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={dateId} className="text-xs">Date</Label>
              <Input
                id={dateId}
                type="date"
                value={editForm.date}
                onChange={(e) =>
                  setEditForm((f) => ({
                    ...f,
                    date: (e.target as HTMLInputElement).value,
                  }))
                }
              />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label htmlFor={vendorTaxIdId} className="text-xs">
                {vendorTaxIdLabel(clientCountry)}
              </Label>
              <Input
                id={vendorTaxIdId}
                value={editForm.vendor_tax_id}
                onChange={(e) =>
                  setEditForm((f) => ({
                    ...f,
                    vendor_tax_id: (e.target as HTMLInputElement).value,
                  }))
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={totalId} className="text-xs">Total</Label>
              <Input
                id={totalId}
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                value={editForm.total}
                onChange={(e) =>
                  setEditForm((f) => ({
                    ...f,
                    total: (e.target as HTMLInputElement).value,
                  }))
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={taxId} className="text-xs">{taxLabel(clientCountry)}</Label>
              <Input
                id={taxId}
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                value={editForm.tax}
                onChange={(e) =>
                  setEditForm((f) => ({
                    ...f,
                    tax: (e.target as HTMLInputElement).value,
                  }))
                }
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label id={categoryLabelId} className="text-xs">Category</Label>
            <Popover
              open={categoryOpen}
              onOpenChange={(open) => {
                setCategoryOpen(open);
                if (!open) setCategorySearch("");
              }}
            >
              <PopoverTrigger
                role="combobox"
                aria-expanded={categoryOpen}
                aria-labelledby={categoryLabelId}
                aria-label="Select category"
                className="flex w-full items-center justify-between rounded-lg border border-input bg-transparent py-2 pr-2 pl-2.5 text-sm h-8 transition-colors focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
              >
                <span
                  className={
                    editForm.category_name
                      ? "truncate"
                      : "text-muted-foreground"
                  }
                >
                  {editForm.category_name
                    ? formatCategoryName(editForm.category_name)
                    : "Select category"}
                </span>
                <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" />
              </PopoverTrigger>
              <PopoverContent className="w-(--anchor-width) p-0" align="start">
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder="Search categories..."
                    value={categorySearch}
                    onValueChange={setCategorySearch}
                  />
                  <CommandList>
                    {filteredCategories.length === 0 && !showCreateCategory && (
                      <CommandEmpty>No categories found.</CommandEmpty>
                    )}
                    <CommandGroup>
                      {filteredCategories.map((c) => (
                        <CommandItem
                          key={c.id}
                          value={c.name}
                          data-checked={
                            editForm.category_name === c.name || undefined
                          }
                          onSelect={() => {
                            setEditForm((f) => ({
                              ...f,
                              category_name: c.name,
                            }));
                            setCategoryOpen(false);
                            setCategorySearch("");
                          }}
                        >
                          {formatCategoryName(c.name)}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                    {showCreateCategory && (
                      <>
                        <CommandSeparator />
                        <CommandGroup>
                          <CommandItem
                            onSelect={() => {
                              const name = categorySearch.trim();
                              setEditForm((f) => ({
                                ...f,
                                category_name: name,
                              }));
                              createCategoryMutation.mutate(name);
                              setCategoryOpen(false);
                              setCategorySearch("");
                            }}
                          >
                            <Plus className="size-4" />
                            Create "{categorySearch.trim()}"
                          </CommandItem>
                        </CommandGroup>
                      </>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={noteId} className="text-xs">Note</Label>
            <Textarea
              id={noteId}
              value={editForm.note}
              onChange={(e) =>
                setEditForm((f) => ({
                  ...f,
                  note: (e.target as HTMLTextAreaElement).value,
                }))
              }
              rows={2}
            />
          </div>

          <Separator />

          {/* Icon-only Discard + Ask client on the left (same chrome as the
              receipt-list row icons — ghost, icon-xs); Confirm & next stays
              as the labelled primary on the right. */}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={() => setConfirmDiscardOpen(true)}
              disabled={discardMutation.isPending}
              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
              aria-label="Discard receipt"
              title="Discard receipt"
              data-testid="btn-discard"
            >
              {discardMutation.isPending ? (
                <Loader2 className="size-3.5 animate-spin" aria-hidden />
              ) : (
                <Trash2 className="size-3.5" aria-hidden />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={() => setQueryOpen(true)}
              aria-label="Ask client about this receipt"
              title="Ask client about this receipt"
            >
              <MessageCircleQuestion className="size-3.5" aria-hidden />
            </Button>
            <Button
              size="sm"
              className="ml-auto"
              onClick={() => confirmMutation.mutate()}
              disabled={confirmMutation.isPending}
              aria-label="Confirm and move to next receipt"
              data-testid="btn-confirm"
            >
              {confirmMutation.isPending ? (
                <Loader2 className="mr-1.5 size-3 animate-spin" />
              ) : (
                <Check className="mr-1.5 size-3" />
              )}
              Confirm &amp; next
            </Button>
          </div>

          {selected.uploaded_by_email && (
            <div className="pt-2 text-xs text-muted-foreground">
              Uploaded by {selected.uploaded_by_email}
            </div>
          )}
        </div>
      </div>

      {/* Dot indicator + prev/next */}
      <footer className="sticky bottom-0 border-t bg-background/95 backdrop-blur px-4 py-2 flex items-center justify-between gap-3">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => goto(-1)}
          disabled={selectedIdx <= 0}
          aria-label="Previous"
          data-testid="btn-prev"
        >
          <ChevronLeft className="size-4" aria-hidden />
          Prev
        </Button>
        <div className="flex items-center gap-1 overflow-x-auto max-w-[50vw]">
          {queue.map((r, i) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setAt(r.id)}
              className={cn(
                "size-2 rounded-full transition-colors shrink-0",
                i === selectedIdx
                  ? "bg-primary"
                  : "bg-muted-foreground/30 hover:bg-muted-foreground/60",
              )}
              aria-label={`Go to receipt ${i + 1}`}
              aria-current={i === selectedIdx ? "step" : undefined}
            />
          ))}
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => goto(1)}
          disabled={selectedIdx >= queue.length - 1}
          aria-label="Next"
          data-testid="btn-next"
        >
          Next
          <ChevronRight className="size-4" aria-hidden />
        </Button>
      </footer>

      {/* Fullscreen image / PDF */}
      <Dialog open={fullscreen} onOpenChange={setFullscreen}>
        <DialogContent className="max-w-[90vw] w-auto p-2 flex items-center justify-center">
          <DialogTitle className="sr-only">Receipt preview</DialogTitle>
          {isPdfPath(selected.file_path) ? (
            <iframe
              src={selected.presigned_url}
              title="Receipt PDF fullscreen"
              className="w-[88vw] h-[85vh] rounded bg-background"
            />
          ) : (
            <img
              src={selected.presigned_url}
              alt="Receipt fullscreen"
              className="max-w-[88vw] max-h-[85vh] w-auto h-auto object-contain"
              style={{ transform: `rotate(${rotation}deg)` }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Ask client — unified composer (matches the receipts-table dialog) */}
      <Dialog open={queryOpen} onOpenChange={setQueryOpen}>
        <DialogContent className="max-w-5xl lg:max-w-6xl xl:max-w-7xl">
          <DialogHeader>
            <DialogTitle>Ask client about this receipt</DialogTitle>
            <DialogDescription>
              Your message is sent via email; the client can reply directly.
            </DialogDescription>
          </DialogHeader>
          {selected && (
            <EmailComposer
              key={selected.id}
              toName={clientMeta?.display_name || "Client"}
              defaultSubject={
                selected.vendor_name
                  ? `Question about a receipt from ${selected.vendor_name}`
                  : "Question about a receipt"
              }
              initialBody=""
              bodyMode="rich"
              bodyLabel="Question"
              bodyRequired
              bodyPlaceholder="e.g. What was this for?"
              previewType="ask_receipt"
              clientId={userId}
              receiptId={selected.id}
              sending={queryMutation.isPending}
              onSend={({ subject, body }) =>
                queryMutation.mutate({ message: body, subject })
              }
              onCancel={() => setQueryOpen(false)}
              sendLabel="Send question"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Discard single receipt confirmation */}
      <ConfirmDialog
        open={confirmDiscardOpen}
        onOpenChange={setConfirmDiscardOpen}
        title="Discard this receipt?"
        description="This can't be undone. The receipt won't be added to the client's confirmed list."
        confirmLabel="Discard"
        destructive
        loading={discardMutation.isPending}
        onConfirm={() =>
          discardMutation.mutate(undefined, {
            onSuccess: () => setConfirmDiscardOpen(false),
          })
        }
      />

      {/* Confirm all modal */}
      <Dialog open={confirmAllOpen} onOpenChange={setConfirmAllOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm {queue.length} receipts?</DialogTitle>
            <DialogDescription>
              This confirms all remaining receipts using their current details.
              You can still edit them later from the main table.
            </DialogDescription>
          </DialogHeader>
          {!isSubscribed && (
            <Badge variant="outline" className="w-fit">
              Free plan: limited to 10 confirmed receipts
            </Badge>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmAllOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => batchConfirmMutation.mutate()}
              disabled={batchConfirmMutation.isPending}
            >
              {batchConfirmMutation.isPending
                ? "Confirming..."
                : `Confirm all ${queue.length}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
